import React, { RefObject, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { formats, generateToolbarSettings, extractValue, onChangeSelection, parseValue } from './RichtextGroupHelpers';
import './../../../../node_modules/react-quill/dist/quill.snow.css';
import IconButton from './../../../shared/buttons/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { updateCustomizedPropAction } from '../../../pages/LayoutEditorPage/actions/layoutEditor.js';
import { updateCustomizedAction } from '../../../pages/ChartEditorPage/actions/chartEditor';
import { setAction } from 'redux/actions/projectConfig';
import lang from '../../../editor/en';
import { RootState } from 'redux/store';
import { UnknownParamsFunction } from 'shared/types/commonPropTypes';
import { onChange as onChangeTooltipText } from 'shared/widgets/helpers/TooltipHelper';

type ModalProps = {
  aggregatedOptions: any;
};

const updateCustomized: Record<string, UnknownParamsFunction> = {
  chart: updateCustomizedAction,
  table: updateCustomizedPropAction,
  layout: updateCustomizedPropAction
};

const RichtextGroupModal = ({ aggregatedOptions }: ModalProps) => {
  const { type } = useSelector((state: RootState) => state.layoutEditorPage);
  const { chart } = useSelector((state: RootState) => state.chartEditorPage);
  const { richtextGroupOptions: richtextOptions } = useSelector<any, any>((state: RootState) => state.projectConfig);
  const dispatch = useDispatch();

  const quillRef = useRef() as RefObject<ReactQuill>;
  const [position, setPosition] = useState({
    top: '200px',
    left: '200px'
  });
  const [inlineSettings, setInlineSettings] = useState({});

  const set = (params: any) => dispatch(setAction(params));
  const updateCustomizedProp = (
    type: string,
    optionProps: string | object,
    val: any,
    extraModulesToLoad?: any,
    index?: number | null,
    skipEmit?: boolean
  ) => {
    return dispatch(updateCustomized[type]({ optionProps, val, extraModulesToLoad, index, skipEmit }));
  };

  useEffect(() => {
    setTimeout(() => {
      if (quillRef?.current) quillRef.current.focus();
    }, 100);

    if (richtextOptions) {
      // Position correctly
      const box = richtextOptions.box;
      const bounding = box.getBoundingClientRect();
      const offsetY = 66; // TODO: remove magic numbers
      let offsetX = 0; // TODO: remove magic numbers
      const container = document.querySelector('.richtext-editor-container');
      const navBar = document.querySelector('.navigation-container-vertical');

      if (navBar) {
        const navSize = navBar.getBoundingClientRect();
        offsetX = navSize.width;
      }
      let left = bounding.left - offsetX,
        top = bounding.top - offsetY;

      if (container) {
        const containerSize = container.getBoundingClientRect();
        if (left + containerSize.width > document.body.clientWidth - containerSize.width) {
          left = document.body.clientWidth - containerSize.width - 10 - offsetX;
        }

        if (top + containerSize.height > document.body.clientHeight - containerSize.height) {
          top = document.body.clientHeight - containerSize.height - 10 - offsetY;
        }
      }
      setPosition({
        left: `${left}px`,
        top: `${top}px`
      });
    }
  }, [richtextOptions]);

  const onClose = () => {
    set({
      richtextGroupOptions: false
    });
  };

  const onChangeInternalSelection = (range: any, source: any, quill: any) => {
    if (range) {
      onChangeSelection(range, source, quill, inlineSettings, setInlineSettings);
    }
  };

  const onChangeStyle = (key: string, value: any, isOptionalArray?: boolean) => {
    const id = richtextOptions.option.id.replace('.text', '');
    const index = richtextOptions.option.richtext?.isArray ? 0 : null;
    updateCustomizedProp(type, { id: `${id}.${key}`, isOptionalArray }, value, null, index);
  };

  const onChange = (value: string, _change: any, source: any) => {
    const val = parseValue(value);
    if (source === 'user') {
      const index = richtextOptions.option.richtext?.isArray ? 0 : null;
      const isTooltipHelper = richtextOptions.option.richtext?.skipUseHTML;

      if (isTooltipHelper) onChangeTooltipText(richtextOptions.option.richtext.type, { val }, aggregatedOptions);
      else {
        updateCustomizedProp(type, richtextOptions.option.id, val, null, index, !isTooltipHelper);
        updateCustomizedProp(type, richtextOptions.option.id.replace('.text', '.useHTML'), true, null, index);
      }
    }
  };

  const options = extractValue(type, richtextOptions?.option, chart, aggregatedOptions);
  const { toolbar, toolbarOptions } = generateToolbarSettings({
    type,
    settings: richtextOptions?.option,
    id: 'toolbar-74e6df45-2835-4dde-b418-324b2e28b770',
    options,
    ref: quillRef,
    onChange: onChangeStyle,
    inlineSettings,
    setInlineSettings,
    aggregatedOptions,
    fields: ['bold', 'underline', 'italic', 'fontSize', 'align', 'color', 'font'],
    className: 'px-0',
    noHighchartOptions: richtextOptions?.option?.richtext?.noHighchartOptions
  });

  const chartBackgroundColor =
    richtextOptions?.option?.id === 'tooltip' ? 'white' : aggregatedOptions?.chart?.backgroundColor ?? 'white';
  const langEntriesKey = (richtextOptions &&
    richtextOptions.option &&
    'option.text.' + richtextOptions.option.pid) as keyof typeof lang.entries;

  let text = options.text ?? '';

  if (richtextOptions?.option?.richtext?.removeWatermark) {
    text = text.replace(' © <a href="http://www.naturalearthdata.com">Natural Earth</a>', '');
  }

  return (
    <>
      {richtextOptions && (
        <div
          className=" w-[340px] absolute z-[9998] bg-white min-h-[120px] shadow-md rounded richtext-editor-container"
          style={position}
        >
          <IconButton
            icon="times"
            className="absolute right-2 top-2 rounded w-8 h-8"
            iconClass="text-base"
            onClick={onClose}
          />

          <div className="p-4">
            <div className="text-ev-blue font-bold text-base">
              Edit {(lang.entries[langEntriesKey ?? ''] ?? '').toLowerCase()} text:
            </div>
            <div>
              <div className="toolbar-container">{toolbar}</div>

              <div className="richtext-editor">
                <ReactQuill
                  theme="snow"
                  defaultValue={text}
                  modules={toolbarOptions}
                  formats={formats}
                  onChange={onChange}
                  onChangeSelection={onChangeInternalSelection}
                  ref={quillRef}
                  style={{
                    ...(options?.style ?? {}),
                    ...{
                      backgroundColor: chartBackgroundColor
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RichtextGroupModal;
